import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getProcessingRuns, getProcessingRun, cancelProcessingRun } from '../../services/adminApi';
import { Button, EmptyState, Spinner, StatusPill, tableStyles } from '../ui';
import type { StatusVariant } from '../ui';

interface RunProgress {
  step: string;
  current?: number;
  total?: number;
}

interface RunStats {
  totalRows: number;
  processedRows: number;
  filteredRows: number;
  channelCount: number;
  totalImpressions: number;
  totalChannelEarnings: number;
}

interface RunOutput {
  masterSheetId?: string;
  masterSheetUrl?: string;
  summarySheetId?: string;
  summarySheetUrl?: string;
  channelSheetsCreated?: number;
  channelSheetsErrors?: string[];
}

interface ChannelMappingInfo {
  channelmeterMatches: number;
  airtableMatches: number;
  manualMatches: number;
  legacySeedMatches: number;
  unmappedChannels: string[];
}

interface ProcessingRunRecord {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  triggeredBy: string;
  month: string;
  monthDisplay: string;
  dryRun: boolean;
  /** 'full' (or absent) = the whole stage-1 run; scoped values are granular re-runs */
  scope?: 'full' | 'masterSummary' | 'trackerDistribution';
  startedAt: string;
  completedAt?: string;
  durationMs?: number;
  progress?: RunProgress;
  stats?: RunStats;
  channelMapping?: ChannelMappingInfo;
  output?: RunOutput;
  reportSource?: 'api' | 'email' | 'upload';
  /** The uploaded GAM CSV this run processed */
  reportUpload?: {
    contentHash: string;
    fileName: string;
    uploadedAt: string;
    /** The month's GAM reports when the run read more than one */
    sources?: Array<{ sourceId: string; fileName: string; contentHash: string; rowCount: number }>;
  };
  error?: string;
  errorDetails?: string;
}

const SCOPE_LABELS: Record<string, string> = {
  masterSummary: 'Master + Summary re-run',
  trackerDistribution: 'Tracker re-distribution',
};

const ProcessingTab: React.FC = () => {
  const [runs, setRuns] = useState<ProcessingRunRecord[]>([]);
  const [loadingRuns, setLoadingRuns] = useState<boolean>(true);
  const [runsError, setRunsError] = useState<string | null>(null);

  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);
  const [expandedRunDetails, setExpandedRunDetails] = useState<ProcessingRunRecord | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);

  const [cancellingRunId, setCancellingRunId] = useState<string | null>(null);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadRuns = useCallback(async (showLoading = true) => {
    if (showLoading) setLoadingRuns(true);
    setRunsError(null);
    try {
      const data = await getProcessingRuns(20);
      setRuns(data.runs || data || []);
    } catch (err: any) {
      setRunsError(err.message || 'Failed to load processing runs.');
    } finally {
      if (showLoading) setLoadingRuns(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Auto-poll when any run is pending/running (no cleanup teardown per cycle)
  useEffect(() => {
    const hasRunning = runs.some((r) => r.status === 'running' || r.status === 'pending');
    if (hasRunning && !pollIntervalRef.current) {
      pollIntervalRef.current = setInterval(() => loadRuns(false), 5000);
    } else if (!hasRunning && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, [runs, loadRuns]);

  // Cleanup polling on unmount only
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  const handleExpandRun = async (runId: string) => {
    if (expandedRunId === runId) {
      setExpandedRunId(null);
      setExpandedRunDetails(null);
      return;
    }
    setExpandedRunId(runId);
    setLoadingDetails(true);
    try {
      const details = await getProcessingRun(runId);
      setExpandedRunDetails(details.run || details);
    } catch {
      setExpandedRunDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCancelRun = async (runId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Don't toggle expand
    if (!window.confirm('Cancel this processing run?')) return;
    setCancellingRunId(runId);
    try {
      await cancelProcessingRun(runId);
      await loadRuns(false);
      // Re-poll after a short delay to catch backend finishing its cleanup
      setTimeout(() => loadRuns(false), 2000);
    } catch (err: any) {
      setRunsError(err.message || 'Failed to cancel run.');
    } finally {
      setCancellingRunId(null);
    }
  };

  const formatDuration = (ms?: number): string => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatCurrency = (amount?: number): string => {
    if (amount === undefined || amount === null) return '-';
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (iso?: string): string => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusVariant = (status: string): StatusVariant => {
    switch (status) {
      case 'completed':
        return 'ok';
      case 'running':
        return 'approved';
      case 'pending':
        return 'pending';
      case 'failed':
        return 'flagged';
      default:
        return 'draft';
    }
  };

  return (
    <div>
      {/* Processing Runs History */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Processing History</h2>
          <Button variant="subtle" size="sm" onClick={() => loadRuns()}>Refresh</Button>
        </div>

        {loadingRuns && (
          <div style={styles.loadingRow}>
            <Spinner label="Loading processing runs…" />
          </div>
        )}
        {runsError && <div style={styles.errorBanner}>{runsError}</div>}

        {!loadingRuns && !runsError && runs.length === 0 && (
          <EmptyState
            brand={false}
            maxWidth={480}
            title="No processing runs yet"
            subtitle="Runs appear here after you upload the month's GAM report and start Direct Sold processing from the pipeline."
          />
        )}

        {!loadingRuns && runs.length > 0 && (
          <div style={tableStyles.wrapScroll}>
            <table style={tableStyles.table}>
              <thead>
                <tr>
                  <th style={tableStyles.th}>Month</th>
                  <th style={tableStyles.th}>Status</th>
                  <th style={tableStyles.th}>Triggered By</th>
                  <th style={tableStyles.th}>Source</th>
                  <th style={tableStyles.th}>Started At</th>
                  <th style={tableStyles.th}>Duration</th>
                  <th style={tableStyles.thNum}>Rows</th>
                  <th style={tableStyles.thNum}>Channels</th>
                  <th style={tableStyles.thNum}>Earnings</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <React.Fragment key={run.id}>
                    <tr
                      onClick={() => handleExpandRun(run.id)}
                      onMouseEnter={(ev) => {
                        if (expandedRunId !== run.id) ev.currentTarget.style.background = tableStyles.rowHoverBackground;
                      }}
                      onMouseLeave={(ev) => {
                        if (expandedRunId !== run.id) ev.currentTarget.style.background = '';
                      }}
                      style={{
                        cursor: 'pointer',
                        background: expandedRunId === run.id ? tableStyles.trExpanded.background : undefined,
                      }}
                    >
                      <td style={styles.td}>
                        {run.monthDisplay || run.month}
                        {run.dryRun && (
                          <StatusPill variant="approved" noDot style={{ marginLeft: 8 }}>
                            Dry run
                          </StatusPill>
                        )}
                        {run.scope && run.scope !== 'full' && (
                          <StatusPill variant="draft" noDot style={{ marginLeft: 8 }}>
                            {SCOPE_LABELS[run.scope] || run.scope}
                          </StatusPill>
                        )}
                      </td>
                      <td style={styles.td}>
                        <StatusPill variant={getStatusVariant(run.status)}>{run.status}</StatusPill>
                        {(run.status === 'running' || run.status === 'pending') && (
                          <>
                            {run.progress && (
                              <div style={styles.progressText}>
                                {run.progress.step}
                                {run.progress.current != null && run.progress.total != null && (
                                  <> ({run.progress.current}/{run.progress.total})</>
                                )}
                              </div>
                            )}
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={(e) => handleCancelRun(run.id, e)}
                              disabled={cancellingRunId === run.id}
                              style={{ marginTop: 4 }}
                            >
                              {cancellingRunId === run.id ? 'Cancelling...' : 'Cancel'}
                            </Button>
                          </>
                        )}
                      </td>
                      <td style={styles.td}>{run.triggeredBy}</td>
                      <td style={{ ...styles.td, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          title={run.reportUpload ? `${run.reportUpload.fileName} (uploaded ${new Date(run.reportUpload.uploadedAt).toLocaleString()})` : undefined}>
                        {run.reportUpload?.fileName || (run.reportSource ? run.reportSource : '—')}
                      </td>
                      <td style={styles.td}>{formatDate(run.startedAt)}</td>
                      <td style={styles.td}>{formatDuration(run.durationMs)}</td>
                      <td style={styles.tdNum}>{run.stats?.processedRows ?? '-'}</td>
                      <td style={styles.tdNum}>{run.stats?.channelCount ?? '-'}</td>
                      <td style={styles.tdNum}>{formatCurrency(run.stats?.totalChannelEarnings)}</td>
                    </tr>
                    {expandedRunId === run.id && (
                      <tr>
                        <td colSpan={9} style={styles.expandedCell}>
                          {loadingDetails ? (
                            <p style={styles.loadingText}>Loading details...</p>
                          ) : expandedRunDetails ? (
                            <div style={styles.detailsGrid}>
                              {/* Report Source (the uploaded GAM CSV this run processed) */}
                              {expandedRunDetails.reportUpload && (
                                <div style={styles.detailSection}>
                                  <h4 style={styles.detailTitle}>
                                    {expandedRunDetails.reportUpload.sources?.length
                                      ? `Report Source (${expandedRunDetails.reportUpload.sources.length} GAM reports, merged)`
                                      : 'Report Source'}
                                  </h4>
                                  {expandedRunDetails.reportUpload.sources?.length ? (
                                    expandedRunDetails.reportUpload.sources.map((source) => (
                                      <p key={source.sourceId} style={styles.detailText}>
                                        {source.fileName} · {source.rowCount.toLocaleString()} rows
                                      </p>
                                    ))
                                  ) : (
                                    <p style={styles.detailText}>{expandedRunDetails.reportUpload.fileName}</p>
                                  )}
                                  <p style={styles.detailText}>
                                    Uploaded {new Date(expandedRunDetails.reportUpload.uploadedAt).toLocaleString()}
                                  </p>
                                  <p style={{ ...styles.detailText, wordBreak: 'break-all' }}>
                                    Hash: {expandedRunDetails.reportUpload.contentHash.slice(0, 16)}…
                                  </p>
                                </div>
                              )}
                              {/* Output Links */}
                              {expandedRunDetails.output && (
                                <div style={styles.detailSection}>
                                  <h4 style={styles.detailTitle}>Output</h4>
                                  {expandedRunDetails.output.masterSheetUrl && (
                                    <p>
                                      <a href={expandedRunDetails.output.masterSheetUrl} target="_blank" rel="noopener noreferrer" style={styles.link}>
                                        Master Sheet
                                      </a>
                                    </p>
                                  )}
                                  {expandedRunDetails.output.summarySheetUrl && (
                                    <p>
                                      <a href={expandedRunDetails.output.summarySheetUrl} target="_blank" rel="noopener noreferrer" style={styles.link}>
                                        Summary Sheet
                                      </a>
                                    </p>
                                  )}
                                  {expandedRunDetails.output.channelSheetsCreated !== undefined && (
                                    <p style={styles.detailText}>
                                      Channel sheets created: {expandedRunDetails.output.channelSheetsCreated}
                                    </p>
                                  )}
                                  {expandedRunDetails.output.channelSheetsErrors && expandedRunDetails.output.channelSheetsErrors.length > 0 && (
                                    <div>
                                      <p style={styles.detailText}>Channel sheet errors:</p>
                                      <ul style={styles.errorList}>
                                        {expandedRunDetails.output.channelSheetsErrors.map((e, i) => (
                                          <li key={i} style={styles.errorListItem}>{e}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Channel Mapping Stats */}
                              {expandedRunDetails.channelMapping && (
                                <div style={styles.detailSection}>
                                  <h4 style={styles.detailTitle}>Channel Mapping</h4>
                                  <p style={styles.detailText}>
                                    ChannelMeter: {expandedRunDetails.channelMapping.channelmeterMatches || 0}
                                  </p>
                                  <p style={styles.detailText}>
                                    Airtable: {expandedRunDetails.channelMapping.airtableMatches || 0}
                                  </p>
                                  <p style={styles.detailText}>
                                    Manual / Legacy: {(expandedRunDetails.channelMapping.manualMatches || 0) + (expandedRunDetails.channelMapping.legacySeedMatches || 0)}
                                  </p>
                                  {expandedRunDetails.channelMapping.unmappedChannels.length > 0 && (
                                    <div>
                                      <p style={styles.detailText}>
                                        Unmapped ({expandedRunDetails.channelMapping.unmappedChannels.length}):
                                      </p>
                                      <ul style={styles.errorList}>
                                        {expandedRunDetails.channelMapping.unmappedChannels.map((ch, i) => (
                                          <li key={i} style={styles.errorListItem}>{ch}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Stats */}
                              {expandedRunDetails.stats && (
                                <div style={styles.detailSection}>
                                  <h4 style={styles.detailTitle}>Statistics</h4>
                                  <p style={styles.detailText}>Total rows: {expandedRunDetails.stats.totalRows}</p>
                                  <p style={styles.detailText}>Processed: {expandedRunDetails.stats.processedRows}</p>
                                  <p style={styles.detailText}>Filtered out: {expandedRunDetails.stats.filteredRows}</p>
                                  <p style={styles.detailText}>
                                    Total impressions: {expandedRunDetails.stats.totalImpressions?.toLocaleString()}
                                  </p>
                                  <p style={styles.detailText}>
                                    Total earnings: {formatCurrency(expandedRunDetails.stats.totalChannelEarnings)}
                                  </p>
                                </div>
                              )}

                              {/* Error */}
                              {expandedRunDetails.error && (
                                <div style={styles.detailSection}>
                                  <h4 style={{ ...styles.detailTitle, color: 'var(--danger)' }}>Error</h4>
                                  <p style={styles.errorText}>{expandedRunDetails.error}</p>
                                  {expandedRunDetails.errorDetails && (
                                    <pre style={styles.errorPre}>{expandedRunDetails.errorDetails}</pre>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p style={styles.emptyText}>No details available.</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  section: {
    background: 'var(--bg-surface-1)',
    borderRadius: '12px',
    border: '1px solid var(--border-1)',
    padding: '24px',
    marginBottom: '24px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  refreshButton: {
    padding: '6px 14px',
    background: 'var(--bg-app)',
    color: 'var(--fg-2)',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  errorText: {
    color: 'var(--danger)',
    fontSize: '13px',
    marginTop: '8px',
  },
  errorBanner: {
    background: 'var(--danger-soft)',
    border: '1px solid var(--cg-terra-200)',
    color: 'var(--danger)',
    borderRadius: 'var(--radius-md)',
    padding: '10px 14px',
    fontSize: 13,
    fontWeight: 500,
  },
  loadingRow: {
    display: 'flex',
    justifyContent: 'center',
    padding: '2rem',
  },
  tdNum: tableStyles.tdNum,
  loadingText: {
    color: 'var(--fg-2)',
    fontSize: '14px',
  },
  emptyText: {
    color: 'var(--fg-3)',
    fontSize: '14px',
    fontStyle: 'italic',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
    borderRadius: '8px',
    border: '1px solid var(--border-1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '13px',
  },
  th: {
    textAlign: 'left' as const,
    padding: '10px 14px',
    background: 'var(--bg-app)',
    borderBottom: '2px solid var(--border-1)',
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    color: 'var(--fg-2)',
    whiteSpace: 'nowrap' as const,
  },
  tr: {
    borderBottom: '1px solid var(--border-1)',
    transition: 'background 0.15s',
  },
  td: {
    padding: '10px 14px',
    color: 'var(--fg-1)',
    whiteSpace: 'nowrap' as const,
    verticalAlign: 'middle' as const,
  },
  progressText: {
    fontSize: '11px',
    color: 'var(--fg-3)',
    marginTop: '4px',
    fontWeight: 400,
  },
  cancelButton: {
    marginTop: '4px',
    padding: '2px 8px',
    background: 'transparent',
    color: 'var(--danger)',
    border: '1px solid var(--danger)',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'var(--font-sans)',
  },
  dryRunBadge: {
    display: 'inline-block',
    marginLeft: '8px',
    padding: '1px 6px',
    borderRadius: '4px',
    background: 'var(--accent-soft)',
    color: 'var(--accent)',
    fontSize: '10px',
    fontWeight: 600,
    verticalAlign: 'middle',
  },
  expandedCell: {
    padding: '16px 24px',
    background: 'var(--accent-soft)',
    borderBottom: '1px solid var(--border-1)',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
  },
  detailSection: {
    background: 'var(--bg-surface-1)',
    borderRadius: '8px',
    padding: '16px',
    border: '1px solid var(--border-1)',
  },
  detailTitle: {
    margin: '0 0 10px 0',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--fg-1)',
  },
  detailText: {
    margin: '4px 0',
    fontSize: '13px',
    color: 'var(--fg-2)',
  },
  link: {
    color: 'var(--accent)',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '13px',
  },
  errorList: {
    margin: '4px 0 0 0',
    paddingLeft: '18px',
  },
  errorListItem: {
    fontSize: '12px',
    color: 'var(--danger)',
    margin: '2px 0',
  },
  errorPre: {
    background: 'var(--bg-app)',
    padding: '8px',
    borderRadius: '6px',
    fontSize: '11px',
    overflow: 'auto' as const,
    maxHeight: '200px',
    color: 'var(--fg-2)',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },
};

export default ProcessingTab;
